// @flow
import React from "react";
import { useSelector, connect } from "react-redux";

import { Translate } from "@quantum/i18n";

import {
  SelecaoCasasDecimais,
  SelecaoPeriodo,
  SelecaoPeriodicidade,
  SelecaoAjusteDias,
  SelecaoRetornoNumerador,
  SelecaoCalculoNumerador,
  SelecaoPeriodicidadeEstatisticaNumerador,
  SelecaoTipoVolatilidade,
  SelecaoEstimacaoVolatilidade,
  SelecaoPeriodicidadeEstatisticaVolatilidade,
  SelecaoDiasUteisPeriodicidadeNumerador,
  SelecaoDiasUteisPeriodicidadeVolatilidade,
  SelecaoTipoRetornoEstatistica,
  SelecaoAtivoSemRisco,
  SelecaoJanelaMovel,
} from "../configuracoes";
import {
  comGeradorDePeriodo,
  getEdicaoMedida,
  getDataBaseSelecionadaAsString,
  getPeriodicidadeEstatisticaNumeradorSelecionada,
  getPeriodicidadeEstatisticaVolatilidadeSelecionada,
  getRetornoNumeradorSelecionado,
  getTipoRetornoConfiguracoesEstatisticaSelecionada,
  isModoSeries,
  analiseValorDaMedida,
} from "../../../state";

type Props = {
  exibirDiasUteisPeriodicidadeNumerador: boolean,
  exibirDiasUteisPeriodicidadeVolatilidade: boolean,
  tipoRetornoNumeradorSelecionado: string,
  tipoRetornoConfiguracoesEstatistica: string,
};

export const processarPeriodoComEstatisticas = comGeradorDePeriodo(
  (state, medidas) => {
    const edicaoMedida = getEdicaoMedida(state);
    const dataBase = getDataBaseSelecionadaAsString(state);
    const modoSeries = isModoSeries(state);

    if (modoSeries) {
      return medidas.map(medidaSelecionada => ({
        ...medidaSelecionada,
        descricaoSelecao: "",
        ativoSemRisco: edicaoMedida.ativoSemRisco,
        retornoNumerador: edicaoMedida.retornoNumerador,
        calculoNumerador: edicaoMedida.calculoNumerador,
        periodicidadeNumerador: edicaoMedida.periodicidadeNumerador,
        diasUteisPeriodicidadeNumerador:
          edicaoMedida.diasUteisPeriodicidadeNumerador,
        tipoVolatilidade: edicaoMedida.tipoVolatilidade,
        estimacaoVolatilidade: edicaoMedida.estimacaoVolatilidade,
        periodicidadeVolatilidade: edicaoMedida.periodicidadeVolatilidade,
        diasUteisPeriodicidadeVolatilidade:
          edicaoMedida.diasUteisPeriodicidadeVolatilidade,
        tipoRetornoConfiguracoesEstatistica:
          edicaoMedida.tipoRetornoConfiguracoesEstatistica,
        janelaMovel: edicaoMedida.janelaMovel,
        ...analiseValorDaMedida(medidaSelecionada, state),
      }));
    } else {
      return medidas.map(medidaSelecionada => ({
        ...medidaSelecionada,
        descricaoSelecao: "",
        periodicidade: edicaoMedida.periodicidade,
        ajusteDias: edicaoMedida.ajusteDias,
        ativoSemRisco: edicaoMedida.ativoSemRisco,
        retornoNumerador: edicaoMedida.retornoNumerador,
        calculoNumerador: edicaoMedida.calculoNumerador,
        periodicidadeNumerador: edicaoMedida.periodicidadeNumerador,
        diasUteisPeriodicidadeNumerador:
          edicaoMedida.diasUteisPeriodicidadeNumerador,
        tipoVolatilidade: edicaoMedida.tipoVolatilidade,
        estimacaoVolatilidade: edicaoMedida.estimacaoVolatilidade,
        periodicidadeVolatilidade: edicaoMedida.periodicidadeVolatilidade,
        diasUteisPeriodicidadeVolatilidade:
          edicaoMedida.diasUteisPeriodicidadeVolatilidade,
        tipoRetornoConfiguracoesEstatistica:
          edicaoMedida.tipoRetornoConfiguracoesEstatistica,
        dataBase: dataBase,
        janelaMovel: edicaoMedida.janelaMovel,
        ...analiseValorDaMedida(medidaSelecionada, state),
      }));
    }
  }
);

function PeriodoComEstatisticas({
  exibirDiasUteisPeriodicidadeNumerador,
  exibirDiasUteisPeriodicidadeVolatilidade,
  tipoRetornoNumeradorSelecionado,
  tipoRetornoConfiguracoesEstatistica,
}: Props) {
  const modoSeries = useSelector(isModoSeries);

  if (modoSeries) {
    return (
      <>
        <div className="ConfiguracaoMedida">
          <SelecaoAtivoSemRisco />
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <h3 className="ConfiguracaoMedida__group-title">
            <Translate>configuracaoRelatorio.numerador</Translate>
          </h3>
          <div className="ConfiguracaoMedida__group--grey">
            <div className="ConfiguracaoMedida__box">
              <SelecaoRetornoNumerador />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoCalculoNumerador
                disabled={
                  tipoRetornoNumeradorSelecionado === "ACUMULADO" ||
                  (tipoRetornoNumeradorSelecionado !== "ACUMULADO" &&
                    tipoRetornoConfiguracoesEstatistica === "LOGARITMICO")
                }
              />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoPeriodicidadeEstatisticaNumerador />
            </div>
            {exibirDiasUteisPeriodicidadeNumerador ? (
              <SelecaoDiasUteisPeriodicidadeNumerador />
            ) : null}
          </div>
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <h3 className="ConfiguracaoMedida__group-title">
            <Translate>configuracaoRelatorio.volatilidade</Translate>
          </h3>
          <div className="ConfiguracaoMedida__group--grey">
            <div className="ConfiguracaoMedida__box">
              <SelecaoTipoVolatilidade />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoEstimacaoVolatilidade />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoPeriodicidadeEstatisticaVolatilidade />
            </div>
            {exibirDiasUteisPeriodicidadeVolatilidade ? (
              <SelecaoDiasUteisPeriodicidadeVolatilidade />
            ) : null}
          </div>
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <h3 className="ConfiguracaoMedida__group-title">
            <Translate>configuracaoRelatorio.retorno</Translate>
          </h3>
          <div className="ConfiguracaoMedida__group--grey">
            <div className="ConfiguracaoMedida__box">
              <SelecaoTipoRetornoEstatistica />
            </div>
          </div>
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <SelecaoJanelaMovel />
        </div>
      </>
    );
  } else {
    return (
      <div className="ConfiguracaoMedida--no-flex">
        <div className="ConfiguracaoMedida--row">
          <SelecaoAtivoSemRisco />
          <SelecaoCasasDecimais />
        </div>

        <div className="ConfiguracaoMedida__horizontal-divider" />

        <div className="ConfiguracaoMedida--row">
          <div className="ConfiguracaoMedida--flex">
            <SelecaoPeriodo exibirPeriodicidade={false} />
          </div>

          <div className="ConfiguracaoMedida">
            <div className="ConfiguracaoMedida__box">
              <SelecaoPeriodicidade />
            </div>

            <div className="ConfiguracaoMedida__box">
              <SelecaoAjusteDias />
            </div>
          </div>

          <div className="ConfiguracaoMedida__vertical-divider" />

          <div className="ConfiguracaoMedida">
            <h3 className="ConfiguracaoMedida__group-title">
              <Translate>configuracaoRelatorio.numerador</Translate>
            </h3>
            <div className="ConfiguracaoMedida__group--grey">
              <div className="ConfiguracaoMedida__box">
                <SelecaoRetornoNumerador />
              </div>
              <div className="ConfiguracaoMedida__box">
                <SelecaoCalculoNumerador
                  disabled={
                    tipoRetornoNumeradorSelecionado === "ACUMULADO" ||
                    (tipoRetornoNumeradorSelecionado !== "ACUMULADO" &&
                      tipoRetornoConfiguracoesEstatistica === "LOGARITMICO")
                  }
                />
              </div>
              <div className="ConfiguracaoMedida__box">
                <SelecaoPeriodicidadeEstatisticaNumerador />
              </div>
              {exibirDiasUteisPeriodicidadeNumerador ? (
                <SelecaoDiasUteisPeriodicidadeNumerador />
              ) : null}
            </div>
          </div>

          <div className="ConfiguracaoMedida__vertical-divider" />

          <div className="ConfiguracaoMedida">
            <h3 className="ConfiguracaoMedida__group-title">
              <Translate>configuracaoRelatorio.volatilidade</Translate>
            </h3>
            <div className="ConfiguracaoMedida__group--grey">
              <div className="ConfiguracaoMedida__box">
                <SelecaoTipoVolatilidade />
              </div>
              <div className="ConfiguracaoMedida__box">
                <SelecaoEstimacaoVolatilidade />
              </div>
              <div className="ConfiguracaoMedida__box">
                <SelecaoPeriodicidadeEstatisticaVolatilidade />
              </div>
              {exibirDiasUteisPeriodicidadeVolatilidade ? (
                <SelecaoDiasUteisPeriodicidadeVolatilidade />
              ) : null}
            </div>
          </div>

          <div className="ConfiguracaoMedida__vertical-divider" />

          <div className="ConfiguracaoMedida">
            <h3 className="ConfiguracaoMedida__group-title">
              <Translate>configuracaoRelatorio.retorno</Translate>
            </h3>
            <div className="ConfiguracaoMedida__group--grey">
              <div className="ConfiguracaoMedida__box">
                <SelecaoTipoRetornoEstatistica />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    exibirDiasUteisPeriodicidadeNumerador:
      getPeriodicidadeEstatisticaNumeradorSelecionada(state) === "ESPECIFICA",
    exibirDiasUteisPeriodicidadeVolatilidade:
      getPeriodicidadeEstatisticaVolatilidadeSelecionada(state) ===
      "ESPECIFICA",
    tipoRetornoNumeradorSelecionado: getRetornoNumeradorSelecionado(state),
    tipoRetornoConfiguracoesEstatistica: getTipoRetornoConfiguracoesEstatisticaSelecionada(
      state
    ),
  }),
  null
)(PeriodoComEstatisticas);
